import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyGetTrackPointsResourceQuery } from '../../../../entities/vehicles/trackPointsResource/redux/trackPointsResource.api';
import transformCoords from '../../../../shared/utils/transformCoords';
import calculateMarkerColor from '../../../../shared/utils/calculateMarkerColor';
import { subscribe, unsubscribe } from '../../../../shared/api/socketLogistic';
import { useLazyGetVehicleIdQuery } from '../../../../entities/vehicles/vehicle/redux/vehicle.api';

const useVehicleCoordinates = (logisticTaskId, vehicles = []) => {
  const [vehiclesCoords, setVehiclesCoords] = useState([]);
  const [fetchTrackPoints] = useLazyGetTrackPointsResourceQuery();
  const [fetchVehicle] = useLazyGetVehicleIdQuery();
  const subscriberRef = useRef(null);

  const topic = useMemo(() => `/topic/logistic-tasks/${logisticTaskId}/vehicle`, [logisticTaskId]);

  const fetchLastCoords = useCallback(async () => {
    const date24hAgo = new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString();

    const results = await Promise.allSettled(
      vehicles.map(async (vehicle) => {
        const res = await fetchTrackPoints({
          params: {
            'vehicleId.equals': vehicle.vehicleId,
            'date.greaterThan': date24hAgo,
            sort: 'date,desc',
            size: '1',
          },
        });

        const vehInfoRes = await fetchVehicle({ id: vehicle.vehicleId });

        return {
          vehicle: { ...vehicle, ...vehInfoRes.data?.data },
          data: res.data?.data,
        };
      }),
    );

    const initialVehicleCoords = [];

    results.forEach((result) => {
      if (result.status === 'fulfilled' && result?.value?.data?.length) {
        const { vehicle, data } = result.value;

        const point = data[0];
        initialVehicleCoords.push({
          vehicleId: vehicle.vehicleId,
          vehicleModelId: vehicle.model?.vehicleModelId,
          coordinates: transformCoords(point.longitude, point.latitude),
          icoColor: calculateMarkerColor(point.date),
          vehicleIconColor: `#${vehicle.customAugmentationEx?.view?.['icon-color']}`,
          num: vehicle.regNum,
        });
      } else {
        initialVehicleCoords.push({
          vehicleId: result.value.vehicle.vehicleId,
          vehicleModelId: result.value.vehicle.model?.vehicleModelId,
          coordinates: null,
          icoColor: '',
          num: result.value.vehicle?.regNum,
          vehicleIconColor: `#${result.value.vehicle.customAugmentationEx?.view?.['icon-color']}`,
        });
      }
    });

    setVehiclesCoords(initialVehicleCoords);
  }, [vehicles, fetchTrackPoints, fetchVehicle]);

  const subscribeToTopic = useCallback(async () => {
    subscriberRef.current = await subscribe(topic, (data) => {
      setVehiclesCoords((prev) => {
        const vehicleIndex = prev.findIndex((v) => v.vehicleId === data.vehicleId);
        if (vehicleIndex === -1) return prev;

        const updated = [...prev];
        updated[vehicleIndex] = {
          ...prev[vehicleIndex],
          coordinates: transformCoords(data.longitude, data.latitude),
          icoColor: calculateMarkerColor(data.date),
        };

        return updated;
      });
    });
  }, [topic, subscribe]);

  useEffect(() => {
    if (!vehicles.length) return;

    fetchLastCoords();
  }, [vehicles, fetchLastCoords]);

  useEffect(() => {
    if (logisticTaskId) subscribeToTopic();

    return () => {
      unsubscribe(subscriberRef.current, topic);
    };
  }, [logisticTaskId, subscribeToTopic]);

  return vehiclesCoords.filter((vehicle) => vehicle.coordinates);
};

export default useVehicleCoordinates;
